import { ReactNode, createContext, useContext, useMemo } from 'react';

/**
 * Para quién se dibuja la pantalla.
 *
 * - `agent`: el asesor de la agencia. Ve el neto del proveedor y su margen.
 * - `retail`: el pasajero, o un cliente B2B al que no se le muestran netos.
 *   Ve el precio de venta y nada más.
 *
 * La búsqueda, las cards y el flujo de reserva son los MISMOS: lo único que
 * cambia es cuánto se revela.
 */
export type Audience = 'agent' | 'retail';

/**
 * `agent` por defecto para que la app actual —que es interna— no dependa de
 * montar el provider. Cuando exista una superficie minorista se envuelve su
 * árbol con `<AudienceProvider audience="retail">`.
 *
 * OJO: esto decide qué se DIBUJA, no qué viaja por la red. El backend hoy
 * manda el neto a propósito (la única superficie es interna); el día que una
 * pantalla la vea un pasajero, el neto tiene que dejar de serializarse, o se
 * lee igual en el network tab.
 */
const AudienceContext = createContext<Audience>('agent');

export function AudienceProvider({
  audience,
  children,
}: {
  audience: Audience;
  children: ReactNode;
}) {
  return (
    <AudienceContext.Provider value={audience}>{children}</AudienceContext.Provider>
  );
}

export function useAudience() {
  const audience = useContext(AudienceContext);
  return useMemo(
    () => ({ audience, isAgent: audience === 'agent' }),
    [audience],
  );
}
